"use client";

import { useEditableForm } from "@/stores/useEditableFormStore";
import { cn } from "@/lib/utils/cn";
import { ChevronDown, Check, Search } from "lucide-react";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { AIGeneratedBadge } from "./AIGeneratedBadge";
import { Typography } from "@/components/common/ui/Typography";
import {
  useFloating,
  useClick,
  useDismiss,
  useInteractions,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  FloatingPortal,
} from "@floating-ui/react";

export interface SelectOption {
  value: string;
  label: string;
  /** Native hover tooltip, for detail that doesn't fit the option width. */
  title?: string;
  disabled?: boolean;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export interface SelectProps {
  ref?: React.Ref<HTMLButtonElement>;
  mode?: "single" | "multiple";
  options?: SelectOption[];
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  error?: string;
  helperText?: React.ReactNode;
  label?: string;
  placeholder?: string;
  formId?: string;
  children?: React.ReactNode;
  isAIInferred?: boolean;
  aiInferredReason?: string;
  inferredBadge?: React.ReactNode;
  onFieldClick?: () => void;
  selectSize?: "default" | "sm" | "xs";
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  searchable?: boolean;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  /** Horizontal alignment of the trigger value text. Defaults to "left". */
  textAlign?: "left" | "center" | "right";
}

const sizeStyles = {
  default: "px-3 py-2 text-base font-medium rounded-lg h-10",
  sm: "px-2.5 py-1.5 text-sm rounded-lg h-9",
  xs: "px-2 py-1.5 text-sm rounded-lg h-8",
} as const;

const iconConfig = {
  default: { rightPad: "pr-10", rightPos: "right-3", iconSize: "w-5 h-5" },
  sm: { rightPad: "pr-8", rightPos: "right-2.5", iconSize: "w-4 h-4" },
  xs: { rightPad: "pr-7", rightPos: "right-2", iconSize: "w-4 h-4" },
} as const;

const dropdownSizeStyles = {
  default: { text: "text-base font-medium", option: "px-3 py-2", checkIcon: "w-4 h-4", checkboxIcon: "w-3 h-3" },
  sm: { text: "text-sm", option: "px-2.5 py-1.5", checkIcon: "w-3.5 h-3.5", checkboxIcon: "w-3 h-3" },
  xs: { text: "text-sm", option: "px-2 py-1.5", checkIcon: "w-3.5 h-3.5", checkboxIcon: "w-3 h-3" },
} as const;

const textAlignClass = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
} as const;

export function Select({
  ref,
  mode = "single",
  options = [],
  error,
  helperText,
  label,
  placeholder,
  formId,
  className = "",
  disabled,
  children,
  value,
  isAIInferred,
  aiInferredReason,
  inferredBadge,
  onFieldClick,
  selectSize = "default",
  required,
  id,
  name,
  searchable = false,
  showSelectAll = false,
  selectAllLabel = "All",
  textAlign = "left",
  onChange,
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>();
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>();

  const {
    forms,
    isEditing: globalIsEditing,
    editableMode: globalEditableMode,
  } = useEditableForm();

  const isEditing = formId
    ? (forms[formId]?.isEditing ?? false)
    : globalIsEditing;

  const editableMode = formId
    ? (forms[formId]?.editableMode ?? false)
    : globalEditableMode;

  const isMultiple = mode === "multiple";

  // Normalize value
  const selectedValues: string[] = useMemo(() => {
    if (isMultiple) {
      return Array.isArray(value) ? value : value ? [value] : [];
    }
    return [];
  }, [isMultiple, value]);

  const selectedSingle = isMultiple ? "" : (typeof value === "string" ? value : "");

  // Filtered options for search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, searchQuery]);

  // Floating UI setup
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!disabled) {
        setIsOpen(open);
        if (!open) setSearchQuery("");
      }
    },
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight }) {
          setDropdownWidth(rects.reference.width);
          setDropdownMaxHeight(Math.max(1, availableHeight));
        },
      }),
    ],
    placement: "bottom-start",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      refs.setReference(node);
      if (typeof ref === "function") {
        ref(node);
      } else if (ref && "current" in ref) {
        (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      }
    },
    [ref, refs],
  );

  const setFloatingRef = useCallback(
    (node: HTMLDivElement | null) => {
      refs.setFloating(node);
    },
    [refs],
  );

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable) {
      // Small delay to let the portal render
      const timer = setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  // Display text for the trigger
  const getDisplayText = (): string => {
    if (isMultiple) {
      if (selectedValues.length === 0) return "";
      if (showSelectAll && selectedValues.length === options.length && options.length > 0) {
        return selectAllLabel;
      }
      if (selectedValues.length === 1) {
        const opt = options.find((o) => o.value === selectedValues[0]);
        return opt?.label || selectedValues[0];
      }
      return `${selectedValues.length} selected`;
    }
    // Single mode
    const opt = options.find((o) => o.value === selectedSingle);
    return opt?.label || "";
  };

  const displayText = getDisplayText();
  const selectedOption = !isMultiple
    ? options.find((o) => o.value === selectedSingle)
    : undefined;

  // Handlers
  const handleSingleSelect = (optValue: string) => {
    onChange?.(optValue);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleMultiToggle = (optValue: string) => {
    const next = selectedValues.includes(optValue)
      ? selectedValues.filter((v) => v !== optValue)
      : [...selectedValues, optValue];
    onChange?.(next);
  };

  const handleSelectAll = () => {
    const enabledOptions = options.filter((o) => !o.disabled);
    const allSelected = enabledOptions.every((o) => selectedValues.includes(o.value));
    if (allSelected) {
      onChange?.([]);
    } else {
      onChange?.(enabledOptions.map((o) => o.value));
    }
  };

  // Validation states
  const isEmpty = isMultiple ? selectedValues.length === 0 : !selectedSingle;
  const isRequiredEmpty = isEmpty && required;
  const isInvalidOption =
    !isMultiple &&
    selectedSingle &&
    options.length > 0 &&
    !options.find((o) => o.value === selectedSingle);

  // Read-only mode for editable forms
  if (editableMode && !isEditing) {
    const readOnlyDisplay = isMultiple
      ? selectedValues
          .map((v) => options.find((o) => o.value === v)?.label || v)
          .join(", ") || "-"
      : displayText || "-";

    return (
      <div className="w-full">
        {label && (
          <Typography as="label" variant="body" weight="medium" color="text-navigator-text-primary" className="inline-flex items-center gap-1.5 mb-2">
            {label}
            {isAIInferred && (inferredBadge || <AIGeneratedBadge reason={aiInferredReason} />)}
          </Typography>
        )}
        <div
          className={cn(
            "text-gray-900 bg-gray-50 border",
            sizeStyles[selectSize],
            textAlignClass[textAlign],
            isRequiredEmpty || isInvalidOption ? "border-red-300 bg-red-100/50" : "border-gray-200",
            onFieldClick && "cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors",
          )}
          onClick={onFieldClick}
          role={onFieldClick ? "button" : undefined}
          tabIndex={onFieldClick ? 0 : undefined}
          onKeyDown={
            onFieldClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") onFieldClick();
                }
              : undefined
          }
        >
          {readOnlyDisplay}
          {isInvalidOption && (
            <span className="text-xs text-red-500 ml-2">
              (invalid option)
            </span>
          )}
        </div>
      </div>
    );
  }

  const icons = iconConfig[selectSize];
  const dropdown = dropdownSizeStyles[selectSize];
  const allEnabledSelected =
    options.filter((o) => !o.disabled).length > 0 &&
    options.filter((o) => !o.disabled).every((o) => selectedValues.includes(o.value));

  return (
    <div className="w-full">
      {label && (
        <Typography as="label" htmlFor={id} variant="body" weight="medium" color="text-navigator-text-primary" className="block mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Typography>
      )}

      <div className="relative">
        {/* Hidden input for form submission */}
        {name && (
          isMultiple
            ? selectedValues.map((v) => (
                <input key={v} type="hidden" name={name} value={v} />
              ))
            : <input type="hidden" name={name} value={selectedSingle} />
        )}

        {/* Trigger button */}
        <button
          ref={setTriggerRef}
          type="button"
          id={id}
          disabled={disabled}
          {...getReferenceProps()}
          className={cn(
            "w-full border transition-colors appearance-none cursor-pointer",
            textAlignClass[textAlign],
            sizeStyles[selectSize],
            icons.rightPad,
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : isRequiredEmpty || isInvalidOption
                ? "border-red-300 bg-red-100/50 focus:border-red-500 focus:ring-red-500/20"
                : "border-gray-200 focus:ring-navigator-primary focus:border-navigator-primary",
            "focus:outline-none focus:ring-1",
            "bg-white text-navigator-text-primary",
            "disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400",
            className,
          )}
        >
          {displayText
            ? (
              <span className="flex items-center gap-2 w-full min-w-0">
                {!isMultiple && selectedOption?.leadingIcon && (
                  <span className="shrink-0 flex items-center">{selectedOption.leadingIcon}</span>
                )}
                <span className={cn("flex-1 min-w-0 truncate font-medium", textAlignClass[textAlign])}>{displayText}</span>
                {!isMultiple && selectedOption?.trailingIcon && (
                  <span className="shrink-0 flex items-center">{selectedOption.trailingIcon}</span>
                )}
              </span>
            )
            : (
              <span className={cn("block w-full text-gray-400", textAlignClass[textAlign])}>
                {placeholder || "Select..."}
              </span>
            )}
        </button>

        {/* Chevron icon */}
        <div className={cn("absolute top-1/2 transform -translate-y-1/2 pointer-events-none", icons.rightPos)}>
          <ChevronDown
            className={cn(
              icons.iconSize,
              "transition-transform",
              error
                ? "text-red-400"
                : disabled
                  ? "text-gray-300"
                  : "text-gray-400",
              isOpen && "rotate-180",
            )}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={setFloatingRef}
            style={{ ...floatingStyles, width: dropdownWidth, maxHeight: dropdownMaxHeight }}
            {...getFloatingProps()}
            className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto overflow-x-hidden"
          >
            {/* Search input */}
            {searchable && (
              <div className="sticky top-0 z-10 bg-white p-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    className={cn("w-full pl-8 pr-2 py-1.5 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-navigator-primary focus:border-navigator-primary bg-white text-gray-900 placeholder:text-gray-400", dropdown.text)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setIsOpen(false);
                        setSearchQuery("");
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {/* Select All option (multi mode only) */}
            {isMultiple && showSelectAll && (
              <>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className={cn("w-full flex items-center gap-3 hover:bg-gray-100 transition-colors", dropdown.text, dropdown.option)}
                >
                  <span
                    className={cn(
                      "flex items-center justify-center w-4 h-4 rounded border shrink-0",
                      allEnabledSelected
                        ? "bg-blue-600 border-blue-600"
                        : "border-gray-300",
                    )}
                  >
                    {allEnabledSelected && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="font-medium text-gray-900">{selectAllLabel}</span>
                </button>
                <div className="border-t border-gray-100" />
              </>
            )}

            {/* Options list */}
            <div className="p-1">
              {filteredOptions.length === 0 ? (
                <div className={cn("text-gray-500", dropdown.text, dropdown.option)}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  if (isMultiple) {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <button
                        key={option.value}
                        type="button"
                        title={option.title}
                        disabled={option.disabled}
                        onClick={() => handleMultiToggle(option.value)}
                        className={cn(
                          "w-full flex items-center justify-between gap-3 rounded-sm font-medium transition-colors",
                          dropdown.text,
                          dropdown.option,
                          option.disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : "hover:bg-gray-100 cursor-pointer",
                        )}
                      >
                        <span className="flex flex-1 items-center gap-3 min-w-0">
                          {/* A disabled option can never be ticked, so its box
                              is a checkbox that means nothing. Callers use
                              disabled options as group headings, where an empty
                              square reads as an unchecked choice. */}
                          {!option.disabled && (
                            <span
                              className={cn(
                                "flex items-center justify-center w-4 h-4 rounded border shrink-0",
                                isSelected
                                  ? "bg-blue-600 border-blue-600"
                                  : "border-gray-300",
                              )}
                            >
                              {isSelected && <Check className="w-3 h-3 text-white" />}
                            </span>
                          )}
                          {option.leadingIcon && (
                            <span className="shrink-0 flex items-center">{option.leadingIcon}</span>
                          )}
                          <span className="min-w-0 flex-1 whitespace-normal break-words text-left leading-snug">
                            {option.label}
                          </span>
                        </span>
                        {option.trailingIcon && (
                          <span className="shrink-0 flex items-center">{option.trailingIcon}</span>
                        )}
                      </button>
                    );
                  }

                  // Single mode
                  const isSelected = selectedSingle === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      title={option.title}
                      disabled={option.disabled}
                      onClick={() => handleSingleSelect(option.value)}
                      className={cn(
                        "w-full flex items-center justify-between gap-3 rounded-sm font-medium transition-colors",
                        dropdown.text,
                        dropdown.option,
                        option.disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "hover:bg-gray-100 cursor-pointer text-navigator-text-primary",
                      )}
                    >
                      <span className="flex flex-1 items-center gap-2 min-w-0">
                        {option.leadingIcon && (
                          <span className="shrink-0 flex items-center">{option.leadingIcon}</span>
                        )}
                        <span className="min-w-0 flex-1 whitespace-normal break-words text-left leading-snug">
                          {option.label}
                        </span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        {option.trailingIcon && (
                          <span className="flex items-center">{option.trailingIcon}</span>
                        )}
                        {isSelected && (
                          <Check className={cn(dropdown.checkIcon, "text-navigator-primary shrink-0")} />
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </FloatingPortal>
      )}

      {error && <Typography as="p" variant="body" color="error" className="mt-1.5">{error}</Typography>}
      {helperText && !error && (
        <Typography as="p" variant="body" color="light" className="mt-1.5">{helperText}</Typography>
      )}
    </div>
  );
}
